// src/services/websocket.ts
// Service WebSocket pour les mises à jour en temps réel

export type WebSocketEventType =
  | 'new_post'
  | 'post_updated'
  | 'post_deleted'
  | 'new_comment'
  | 'comment_updated'
  | 'comment_deleted'
  | 'new_reaction'
  | 'reaction_removed'
  | 'new_notification'
  | 'user_online'
  | 'user_offline'
  | 'typing'
  | 'connection_established';

export interface WebSocketMessage<T = unknown> {
  type: WebSocketEventType;
  payload: T;
  timestamp: string;
  userId?: number;
}

export interface WebSocketConfig {
  url: string;
  token?: string;
  reconnectAttempts?: number;
  reconnectInterval?: number;
  heartbeatInterval?: number;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
  onMessage?: (message: WebSocketMessage) => void;
  onReconnect?: (attempt: number) => void;
}

class WebSocketService {
  private socket: WebSocket | null = null;
  private config: WebSocketConfig | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 3000;
  private heartbeatInterval = 30000;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private messageHandlers: Map<WebSocketEventType, Set<(payload: unknown) => void>> = new Map();
  private isManualClose = false;

  connect(config: WebSocketConfig): void {
    this.config = config;
    this.maxReconnectAttempts = config.reconnectAttempts ?? 5;
    this.reconnectInterval = config.reconnectInterval ?? 3000;
    this.heartbeatInterval = config.heartbeatInterval ?? 30000;
    this.isManualClose = false;

    this.createConnection();
  }

  private createConnection(): void {
    if (!this.config) return;

    try {
      // Ajouter le token à l'URL si fourni
      let url = this.config.url;
      if (this.config.token) {
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}token=${this.config.token}`;
      }

      this.socket = new WebSocket(url);

      this.socket.onopen = () => {
        console.log('[WebSocket] Connected');
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.config?.onOpen?.();
      };

      this.socket.onclose = (event) => {
        console.log('[WebSocket] Disconnected', event.code, event.reason);
        this.stopHeartbeat();
        this.config?.onClose?.();

        if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.socket.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        this.config?.onError?.(error);
      };

      this.socket.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (err) {
          console.error('[WebSocket] Failed to parse message:', err);
        }
      };
    } catch (error) {
      console.error('[WebSocket] Failed to connect:', error);
      this.scheduleReconnect();
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    // Appeler le handler global
    this.config?.onMessage?.(message);

    // Appeler les handlers spécifiques au type
    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(message.payload);
        } catch (err) {
          console.error(`[WebSocket] Handler error for ${message.type}:`, err);
        }
      });
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, this.heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.config?.onReconnect?.(this.reconnectAttempts);

    this.reconnectTimer = setTimeout(() => {
      this.createConnection();
    }, delay);
  }

  disconnect(): void {
    this.isManualClose = true;
    this.stopHeartbeat();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.messageHandlers.clear();
  }

  send<T>(type: WebSocketEventType, payload: T): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type, payload, timestamp: new Date().toISOString() }));
    } else {
      console.warn('[WebSocket] Cannot send message: not connected');
    }
  }

  subscribe<T>(type: WebSocketEventType, handler: (payload: T) => void): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }

    const handlers = this.messageHandlers.get(type)!;
    handlers.add(handler as (payload: unknown) => void);

    // Retourner une fonction de désabonnement
    return () => {
      handlers.delete(handler as (payload: unknown) => void);
      if (handlers.size === 0) {
        this.messageHandlers.delete(type);
      }
    };
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  getState(): number {
    return this.socket?.readyState ?? WebSocket.CLOSED;
  }
}

// Instance singleton
export const websocketService = new WebSocketService();

export default websocketService;
